import { GlobalSettingsKeys } from '../models/GlobalSettingsKeys';
import { Environment } from '../../config/environment';
import { AnalyticsApi } from './AnalyticsApi';
import { v4 as uuidV4 } from 'uuid';
import { formatDate } from '../../Business/utility/DateFormatter';
import { SysCalls } from '../../syscalls/Syscalls';
import * as applicationInfo from '../../config/application-info.json';
import { LogSubcategories, MainCategories, PropertyKeys, TrackSubcategories } from './ClientEvents';
import { Registry } from '@lightningjs/sdk';
import { addProperty } from './AnalyticsUtil';
import { Backend } from '../Backend';
import { IApplicationInfo, IDeviceInfo, ILoginContext, IPageContext, IProperty } from '../models/AndromeTV.types';
import { ErrorResponse } from '../models/ErrorResponse';

// TODO: move these types back to AndromeTv.types
export interface LogCategory {
  type: MainCategories;
  subType: string;
}
/** interface ClientEventEntry */
export interface IClientEventEntry {
  id: string;
  category: LogCategory;
  timestamp: string;
  loginContext?: ILoginContext;
  pageContext?: IPageContext;
  properties: IProperty[];
}

/** interface ClientEvents */
export interface IClientEvents {
  device: IDeviceInfo;
  app: IApplicationInfo;
  events: IClientEventEntry[];
}

export class AnalyticsService {
  private _backend: Backend;
  private _settings: { flushPeriod: number; batchSize: number; maxFailed: number };
  private _currentEvents: IClientEventEntry[];
  private _failedRequests: IClientEvents[];
  private _flushTimeout: number | undefined;
  private _pageContext: IPageContext | undefined;

  constructor(backend: Backend) {
    this._backend = backend;
    this._settings = {
      flushPeriod: 300000,
      batchSize: 50,
      maxFailed: 10,
    };

    this._currentEvents = [];

    this._failedRequests = [];
    this._flushTimeout = undefined;
  }

  private updateGlobalSettings() {
    this._settings = {
      flushPeriod: this._backend.globalSettings.numberValue(GlobalSettingsKeys.analyticsFlushPeriod) || 300000,
      batchSize: this._backend.globalSettings.numberValue(GlobalSettingsKeys.analyticsPostBatchSize) || 50,
      maxFailed: this._backend.globalSettings.numberValue(GlobalSettingsKeys.maxNrOfFailedAnalytics) || 10,
    };
  }

  private _addEvent(event: IClientEventEntry) {
    this._currentEvents.push(event);
    if (this._currentEvents.length >= this._settings.batchSize) {
      this._sendBatch();
    } else {
      this._setFlushTimeout();
    }
  }

  private _setFlushTimeout() {
    if (!this._flushTimeout) {
      this._flushTimeout = Registry.setTimeout(() => {
        this._flushTimeout = undefined;
        this._sendBatch();
      }, this._settings.flushPeriod);
    }
  }

  private _clearFlushTimeout() {
    if (this._flushTimeout) {
      Registry.clearTimeout(this._flushTimeout);
      this._flushTimeout = undefined;
    }
  }

  public forceSendAnalytics() {
    this._sendBatch();
  }

  private _sendBatch() {
    this.updateGlobalSettings();
    this._clearFlushTimeout();

    this._logFailedRequest().then(() => {
      const events = this._currentEvents.splice(0);
      /**
       * @type {IClientEvents}
       */
      const request: IClientEvents = {
        device: this.deviceInfo,
        app: this.applicationInfo,
        events,
      };

      AnalyticsApi.log(this._backend, request).catch(err => {
        this._addFailedBatch(request, err);
      });
    });
  }

  private _logFailedRequest(): Promise<number> {
    if (this._failedRequests.length > 0 && this._failedRequests[0]) {
      return AnalyticsApi.log(this._backend, this._failedRequests[0])
        .then(() => {
          this._failedRequests.splice(0, 1);
          if (this._failedRequests.length > 0) {
            return this._logFailedRequest();
          } else {
            return Promise.resolve(this._failedRequests.length);
          }
        })
        .catch(() => Promise.resolve(this._failedRequests.length));
    } else {
      return Promise.resolve(this._failedRequests.length);
    }
  }

  private _addFailedBatch(failed: IClientEvents, err: ErrorResponse) {
    if (this._failedRequests.length === this._settings.maxFailed) {
      this._failedRequests.splice(0, 1);
    }
    this._addLogError(failed, err);
    this._failedRequests.push(failed);
  }

  private _addLogError(request: IClientEvents, err: ErrorResponse) {
    const properties: IProperty[] = [];
    addProperty(PropertyKeys.errorId, err.errorId, [], properties);
    addProperty(PropertyKeys.errorCode, err.code, [], properties);
    addProperty(PropertyKeys.errorMessage, err.message, [], properties);
    const entry: IClientEventEntry = {
      id: uuidV4(),
      category: { type: MainCategories.LOG, subType: LogSubcategories.LOG_LOGGING_ERROR },
      timestamp: formatDate(new Date(), true),
      loginContext: this.loginContext,
      pageContext: undefined,
      properties: properties,
    };
    request.events.push(entry);
  }

  get deviceInfo(): IDeviceInfo {
    return {
      deviceId: this._backend.deviceProfile ? this._backend.deviceProfile.id : '',
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      model: SysCalls.modelName(),
      platform: Environment.system,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      os: SysCalls.platformVersion(),
    };
  }

  get applicationInfo(): IApplicationInfo {
    return {
      name: applicationInfo.name,
      version: applicationInfo.version,
      build: applicationInfo.buildTime,
    };
  }

  createClientEventEntry(logCategory: LogCategory, properties: IProperty[]) {
    const event: IClientEventEntry = {
      id: uuidV4(),
      category: logCategory,
      timestamp: formatDate(new Date(), true),
      loginContext: this.loginContext,
      pageContext: this._pageContext,
      properties: properties,
    };
    this._addEvent(event);
  }

  get loginContext(): ILoginContext | undefined {
    if (this._backend.sessionInfo) {
      return {
        countryCode: '', // backend overwrites value based on accept-language http header
        userId: this._backend.sessionInfo.customer.loginName ? this._backend.sessionInfo.customer.loginName : '',
        customerId: this._backend.sessionInfo.customer.id,
        deviceId: this._backend.sessionInfo.device.id,
        profileId: this._backend.deviceProfile?.id,
        catalogId: this._backend.deviceProfile?.profile.catalog,
        entitlementId: this._backend.sessionInfo.device.entitlementId,
      };
    } else {
      // Anonymous not supported yet
      return undefined;
    }
  }

  trackPageView(url: string, title: string | undefined = undefined) {
    this._pageContext = {
      pageURL: url,
      pageTitle: title || undefined,
      pageLocale: this._backend.locale,
    };
    this.createClientEventEntry({ type: MainCategories.TRACK, subType: TrackSubcategories.PAGE_VIEW }, []);
  }
}
